import { useEffect, useMemo, useState } from "react";
import { Coffee } from "lucide-react";
import {
  formatLocalClock,
  formatLocalClockParts,
  isUsageCurrent,
  queueUsageText,
  usageCurrentUntil,
  type ClaudeUsageCheck,
  type ClaudeUsageSnapshot,
  type GroomingQueueEstimate,
} from "@ttc-spectree/toolbox-contract";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * The Queue screen's strip (epic PI-SPEC-SW-E284; rewritten for epic
 * PI-SPEC-SW-E290, feature PI-SPEC-SW-1479, Claude Design frame B1):
 * - "Keeping this computer awake until the queue finishes." while the app
 *   holds the computer awake (`state.keepingAwake`, UX-PM-03), with "The
 *   screen can still turn off. You can leave it running.";
 * - "Claude usage", a small meter and "62% of your 5-hour allowance · 31% of
 *   this week's · as of 7:41 AM" from the contract's wording sheet (UX-PM-07),
 *   only while the reading is current (`isUsageCurrent`, PKG-PM-C Mac walk
 *   2026-10-23: a Wednesday reading was shown on Friday as if it were live).
 *   Otherwise it asks the app to refresh (`refreshUsage`) and says "Checking
 *   your Claude usage…", or "Not known right now · last checked …" when the
 *   app cannot refresh or the refresh failed (the app's `usageCheck`, or the
 *   refresh call itself rejecting). Never an old percentage, not even until
 *   the next half-minute tick: the strip re-renders at the reading's own
 *   deadline (`nextUsageDeadline`). Each refresh is a short Claude Code call
 *   on the person's own seat, so none is asked for while a run is in flight
 *   (runs report usage themselves; the app ignores the call) or while the
 *   page is hidden. "Not known yet" before the first reading;
 * - "This queue needs about 12% of your 5-hour window (estimate)" when the
 *   app has enough past runs, hidden while Up next is empty (UX-PM-21,
 *   feature PI-SPEC-SW-1457).
 */

/** The awake sentence and its note (frame B1). */
export const QUEUE_AWAKE_TEXT = "Keeping this computer awake until the queue finishes.";
export const QUEUE_AWAKE_NOTE = "The screen can still turn off. You can leave it running.";

/** The usage words before the first reading. */
export const USAGE_NOT_KNOWN = "Not known yet";
/** While the app refreshes a reading that is not current. */
export const USAGE_CHECKING = "Checking your Claude usage…";
/** When a reading is not current and cannot be refreshed now. */
export const USAGE_NOT_KNOWN_NOW = "Not known right now";
/** A failed refresh is tried again after this long. */
export const USAGE_RETRY_MS = 60_000;

/** A fraction as a whole percentage: 0.4 → "40%". */
export function percent(fraction: number): string {
  return `${String(Math.round(fraction * 100))}%`;
}

/**
 * The usage words. A current reading: "62% of your 5-hour allowance · 31% of
 * this week's · as of 7:41 AM" (a current reading with neither window is
 * "Not known yet", with no time). One that is not: "Checking your Claude
 * usage…" while the app refreshes it (`refreshable` and no failed check),
 * else "Not known right now · last checked Wed at 12:17 PM": the later of
 * the reading and a failed check. No reading: "Checking…" while a check
 * runs, else "Not known yet". Pure; exported for tests.
 */
export function usageText(
  usage: ClaudeUsageSnapshot | null,
  options: { check?: ClaudeUsageCheck | null; refreshable?: boolean; now?: number } = {}
): string {
  const now = options.now ?? Date.now();
  const failed = options.check?.status === "failed";
  if (usage && isUsageCurrent(usage, now)) {
    const words = queueUsageText(usage);
    if (words === null) return USAGE_NOT_KNOWN;
    const at = formatLocalClockParts(usage.observedAt, { now });
    return at ? `${words} · as of ${at.day ? `${at.day} ` : ""}${at.time}` : words;
  }
  if (options.check?.status === "checking" || (options.refreshable === true && !failed)) return USAGE_CHECKING;
  if (!usage) return failed ? USAGE_NOT_KNOWN_NOW : USAGE_NOT_KNOWN;
  const checkedAt = failed && options.check ? options.check.at : null;
  const lastIso = checkedAt !== null && Date.parse(checkedAt) > Date.parse(usage.observedAt) ? checkedAt : usage.observedAt;
  const last = formatLocalClock(lastIso, { now });
  return last ? `${USAGE_NOT_KNOWN_NOW} · last checked ${last}` : USAGE_NOT_KNOWN_NOW;
}

/**
 * The meter's fill, 0-1: the 5-hour window, else the week's; null when
 * neither is known or the reading is not current. Pure; exported for tests.
 */
export function usageFill(usage: ClaudeUsageSnapshot | null, now: number = Date.now()): number | null {
  if (!isUsageCurrent(usage, now)) return null;
  const window = usage?.fiveHour ?? usage?.sevenDay ?? null;
  return window ? Math.min(1, Math.max(0, window.utilization)) : null;
}

/**
 * The estimate sentence, or null when there are too few past runs to estimate
 * or nothing is waiting (`waiting` is the Up next count; omitted means unknown,
 * so the estimate shows). Pure; exported for tests.
 */
export function estimateText(estimate: GroomingQueueEstimate | null, waiting?: number): string | null {
  if (waiting === 0) return null;
  if (estimate?.queueFiveHour === null || estimate?.queueFiveHour === undefined) return null;
  return `This queue needs about ${percent(estimate.queueFiveHour)} of your 5-hour window (estimate)`;
}

/**
 * The next moment the strip's words change on their own: the reading stops
 * counting as current (`usageCurrentUntil`: five minutes old, or a window resets)
 * or a failed refresh is due again ({@link USAGE_RETRY_MS}); null when none
 * is after `now`. Pure; exported for tests.
 */
export function nextUsageDeadline(usage: ClaudeUsageSnapshot | null, check: ClaudeUsageCheck | null, now: number): number | null {
  const candidates: number[] = [];
  const until = usageCurrentUntil(usage);
  if (until !== null) candidates.push(until);
  if (check?.status === "failed") candidates.push(Date.parse(check.at) + USAGE_RETRY_MS);
  const future = candidates.filter((at) => Number.isFinite(at) && at > now);
  return future.length > 0 ? Math.min(...future) : null;
}

export interface QueueUsageLineProps {
  usage: ClaudeUsageSnapshot | null;
  estimate: GroomingQueueEstimate | null;
  /** How many items wait in Up next; 0 hides the estimate. */
  waiting?: number;
  /** True while the app holds the computer awake; anything else hides the awake sentence. */
  keepingAwake?: boolean | undefined;
  /** The app's usage refresh (`usageCheck` in the queue state). */
  usageCheck?: ClaudeUsageCheck | null | undefined;
  /** True while a run is in flight: runs report usage themselves, so no refresh is asked for. */
  runsInFlight?: boolean;
  /** Ask the app to refresh the reading; returns false when it cannot. `onRejected` runs when the call fails. Absent when the app has no refresh. */
  onRefreshUsage?: (onRejected: () => void) => boolean;
  className?: string;
}

/**
 * The clock the strip reads: every half minute, and exactly at the next
 * {@link nextUsageDeadline}, so a reading stops counting as current on time
 * and a failed refresh is tried again on time, with or without a queue event.
 */
function useNow(usage: ClaudeUsageSnapshot | null, check: ClaudeUsageCheck | null, intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, intervalMs);
    return () => {
      clearInterval(timer);
    };
  }, [intervalMs]);
  // Coming back to a hidden page re-reads the clock, so a refresh can start then.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") setNow(Date.now());
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);
  useEffect(() => {
    const deadline = nextUsageDeadline(usage, check, Date.now());
    if (deadline === null) return;
    const timer = setTimeout(() => {
      setNow(Date.now());
    }, deadline - Date.now() + 1);
    return () => {
      clearTimeout(timer);
    };
  }, [usage, check, now]);
  return now;
}

export function QueueUsageLine({
  usage,
  estimate,
  waiting,
  keepingAwake,
  usageCheck = null,
  runsInFlight = false,
  onRefreshUsage,
  className,
}: QueueUsageLineProps) {
  // A refresh call that rejected before the app published a `usageCheck` counts as a failed check here.
  const [rejectedAt, setRejectedAt] = useState<string | null>(null);
  const check = useMemo<ClaudeUsageCheck | null>(
    () => usageCheck ?? (rejectedAt ? { status: "failed", at: rejectedAt } : null),
    [usageCheck, rejectedAt]
  );
  const now = useNow(usage, check);
  const current = isUsageCurrent(usage, now);
  const [callable, setCallable] = useState(onRefreshUsage !== undefined);
  const refreshable = callable && onRefreshUsage !== undefined && !runsInFlight;
  const checking = check?.status === "checking";
  const failedAt = check?.status === "failed" ? Date.parse(check.at) : Number.NaN;
  const retryDue = Number.isNaN(failedAt) || now - failedAt >= USAGE_RETRY_MS;
  // A reading that is not current is refreshed, never shown (PKG-PM-C Mac walk, 2026-10-23).
  useEffect(() => {
    if (current || checking || !retryDue || !onRefreshUsage || runsInFlight || document.visibilityState === "hidden") return;
    setRejectedAt(null);
    setCallable(
      onRefreshUsage(() => {
        setRejectedAt(new Date().toISOString());
      })
    );
  }, [current, checking, retryDue, onRefreshUsage, runsInFlight, now]);
  const observed = usage ? Date.parse(usage.observedAt) : Number.NaN;
  const title = Number.isNaN(observed)
    ? undefined
    : `Last reading ${new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(observed))}`;
  const estimateLine = estimateText(estimate, waiting);
  const fill = usageFill(usage, now);
  // A failed refresh shows as failed until it is tried again.
  const words = usageText(usage, { check: check?.status === "failed" && retryDue ? null : check, refreshable, now });
  const awake = keepingAwake === true;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-[10px] border border-[#1F1F23] bg-[#0E0E10] px-4 py-2.5",
        className
      )}
      data-testid="queue-strip"
    >
      {awake && (
        <>
          <p className={cn("flex min-w-0 items-center gap-2", TOOLBOX_TEXT.content)} data-testid="queue-awake">
            <Coffee className="h-4 w-4 flex-none text-[#6CC4FF]" aria-hidden="true" />
            <span className="font-semibold text-white">{QUEUE_AWAKE_TEXT}</span>
            <span className="text-muted-foreground">{QUEUE_AWAKE_NOTE}</span>
          </p>
          <span className={TOOLBOX_TEXT.faint} aria-hidden="true">
            |
          </span>
        </>
      )}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground" data-testid="queue-usage">
        <span>Claude usage</span>
        {fill !== null && (
          <span className="h-1.5 w-16 overflow-hidden rounded-full bg-[#27272A]" aria-hidden="true" data-testid="queue-usage-meter">
            <span className="block h-full rounded-full bg-[#6CC4FF]" style={{ width: `${String(Math.round(fill * 100))}%` }} />
          </span>
        )}
        <span className="text-foreground" title={title} data-testid="queue-usage-text">
          {words}
        </span>
        {estimateLine && (
          <>
            <span className={TOOLBOX_TEXT.faint} aria-hidden="true">
              ·
            </span>
            <span className={TOOLBOX_TEXT.faint} data-testid="queue-usage-estimate">
              {estimateLine}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
